// ==================== NOTIFICATION SOUND UTILITY ====================
/**
 * SAFE PATCH: Utilitário para notificações sonoras e vibração.
 * Usado para alertar o prestador quando cliente faz check-in.
 */

// Criar contexto de áudio uma vez (singleton)
let audioContext = null;

/**
 * Inicializa o AudioContext (precisa de interação do usuário primeiro).
 */
export const initAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Toca um som de notificação usando Web Audio API.
 * Não requer arquivos de áudio externos.
 * 
 * @param {string} type - Tipo do som: 'checkin', 'alert', 'success'
 */
export const playNotificationSound = (type = 'checkin') => {
  try {
    const ctx = initAudioContext();
    
    // Resumir contexto se estiver suspenso (requer interação do usuário)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Configurar som baseado no tipo
    switch (type) {
      case 'checkin':
        // Som de "ding-dong" amigável para check-in
        oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5
        oscillator.frequency.setValueAtTime(1100, ctx.currentTime + 0.15); // C#6
        oscillator.frequency.setValueAtTime(880, ctx.currentTime + 0.3); // A5
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.5);
        break;
        
      case 'alert':
        // Som de alerta mais urgente
        oscillator.frequency.setValueAtTime(600, ctx.currentTime);
        oscillator.frequency.setValueAtTime(800, ctx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(600, ctx.currentTime + 0.2);
        oscillator.frequency.setValueAtTime(800, ctx.currentTime + 0.3);
        gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.4);
        break;
        
      case 'success':
        // Som de sucesso
        oscillator.frequency.setValueAtTime(523, ctx.currentTime); // C5
        oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1); // E5
        oscillator.frequency.setValueAtTime(784, ctx.currentTime + 0.2); // G5
        gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.4);
        break;
        
      default:
        // Som padrão
        oscillator.frequency.setValueAtTime(800, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
    }
    
    return true;
  } catch (error) {
    console.warn('[NotificationSound] Erro ao tocar som:', error);
    return false;
  }
};

/**
 * Vibra o dispositivo se suportado.
 * 
 * @param {number|number[]} pattern - Duração em ms ou padrão [vibra, pausa, vibra, ...]
 */
export const vibrateDevice = (pattern = 200) => {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('[NotificationSound] Vibração não suportada:', error);
    return false;
  }
};

/**
 * Notificação completa: som + vibração.
 * 
 * @param {string} type - Tipo do som
 * @param {number|number[]} vibrationPattern - Padrão de vibração
 */
export const notifyWithSoundAndVibration = (type = 'checkin', vibrationPattern = [100, 50, 100]) => {
  const soundPlayed = playNotificationSound(type);
  const vibrated = vibrateDevice(vibrationPattern);
  
  return { soundPlayed, vibrated };
};

/**
 * Solicita permissão para notificações do navegador (opcional).
 * Pode ser usado para notificações push futuras.
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'not_supported';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }
  
  return 'denied';
};

/**
 * Mostra notificação do navegador (se permitido).
 */
export const showBrowserNotification = (title, options = {}) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        ...options
      });
      return true;
    } catch (error) {
      console.warn('[NotificationSound] Erro ao mostrar notificação:', error);
    }
  }
  return false;
};
